import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {ClientOnly} from '../../ClientOnly'
import styles from '../Assignees.module.css'
import {AuthorActions} from './AuthorActions'
import {ReviewerActions} from './ReviewerActions'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useReviewersLiveUpdates} from '../../../hooks/use-reviewers-live-updates'
import {pullRequestsLayoutRoute} from '../../../routes/layout-route'
import {useReviewRequestsQuery} from '../data/route-queries'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isPRAuthor, isPRReviewer} from './action-item-helpers'
import {MergeMethodContextProvider} from '@github-ui/mergebox'
import {
  useMergeStatusButtonData,
  type ValidatedMergeStatusButtonData,
} from '../../../hooks/use-merge-status-button-data'

export function ActionItemsSection() {
  return (
    <ErrorBoundary
      boundaryName="ActionItemsSection"
      fallback={<span className={styles.emptyState}>Unable to load action items</span>}
    >
      <Suspense fallback={<UserSectionSkeleton placeholderRows={2} />}>
        <ClientOnly serverFallback={<UserSectionSkeleton placeholderRows={2} />}>
          <ActionItemsContent />
        </ClientOnly>
      </Suspense>
    </ErrorBoundary>
  )
}

function ActionItemsContent() {
  const {data: reviewers} = useReviewRequestsQuery()
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  useReviewersLiveUpdates(layoutData.aliveChannels)

  const {pullRequest, mergeStatusButtonData} = layoutData
  const viewerLogin = getCurrentUserLogin() ?? ''
  const authorLogin = pullRequest.author.login

  const validateMergeStatusButtonData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)

  return (
    <ActionItemsComponent
      reviewers={reviewers}
      viewerLogin={viewerLogin}
      authorLogin={authorLogin}
      mergeStatusButtonData={validateMergeStatusButtonData}
    />
  )
}

type ActionItemsComponentProps = {
  reviewers: ReturnType<typeof useReviewRequestsQuery>['data']
  viewerLogin: string
  authorLogin: string
  mergeStatusButtonData: ValidatedMergeStatusButtonData | null
}

function ActionItemsComponent({reviewers, viewerLogin, authorLogin, mergeStatusButtonData}: ActionItemsComponentProps) {
  const isAuthor = isPRAuthor(viewerLogin, authorLogin)
  const isReviewer = isPRReviewer(viewerLogin, reviewers)

  return (
    <div>
      {isAuthor && mergeStatusButtonData && (
        // TODO - there is an edge case where there is no validated merge status data due to the opt out flag
        <MergeMethodContextProvider defaultMergeMethod={mergeStatusButtonData.defaultMergeMethod}>
          <AuthorActions
            viewerLogin={viewerLogin}
            reviewers={reviewers}
            mergeStatusButtonData={mergeStatusButtonData}
          />
        </MergeMethodContextProvider>
      )}
      {!isAuthor && <ReviewerActions reviewers={reviewers} viewerLogin={viewerLogin} isReviewer={isReviewer} />}
    </div>
  )
}
